// ExportPersonsDrawer.tsx
import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';

import { ApolloClient, DocumentNode, useApolloClient } from '@apollo/client';
import { IDynamicFormWidgetSection } from '@components/dynamic-form/types';
import { normalizeDynamicFormDrawerValues } from '@components/dynamic-form/utils';
import ToastErrorMessage from '@components/toast/ToastErrorMessage';
import ToastSuccessMessage from '@components/toast/ToastSuccessMessage';
import { DEFAULT_ERROR_TEXT } from '@utils';
import { downloadExcelFile } from '@utils/excel/download-json-excel-service';

import {
  ALL,
  exportCurrencyDrawerSections,
  exportCurrencyExcelColumnWidths,
  exportCurrencyExcelHeaderGroups,
  exportCurrencyExcelHeaders,
  exportCurrencyExcelTemplateStyles
} from '@common/constants';

import { searchExchangeRates } from '../services';
import { IBaseDrawerProps, IExportCurrencyDrawerFilters } from '../types';

import DateService from 'src/core/services/DateService';
import DynamicFormWidget from '@components/dynamic-form/DynamicFormWidget';
import { useDrawerSubmission } from '@common/hooks';
import { IAbstractRecord } from '@models';
import { formatForFileName, normalizeMulti } from './exportReinsurers.drawer';
import { useGetCurrencies } from '@common/slices';

export const mapItemsToExcelData = (data: IAbstractRecord[]) => {
  return (data ?? [])
    .map((p: any) => {
      return {
        baseCurrency: p.baseCurrencyNavigation.title ?? '',
        targetCurrency: p.targetCurrencyNavigation.title ?? '',
        rate: p.rate,
        effectiveDate: p.effectiveDate
      };
    })
    .sort((a: any, b: any) =>
      String(a.code || '').localeCompare(String(b.code || ''))
    );
};

export const fetchAllCurrencies = async (
  client: ApolloClient<any>,
  query: DocumentNode,
  vars: {
    baseCurrency: string[] | null;
    effectiveDateFrom: any | null;
    effectiveDateTo: any | null;
    keywordSearch: string | null;
    OrderBy: any | null;
  }
) => {
  const pageSize = 500;
  let pageNumber = 1;
  const all: any[] = [];
  let hasMore = true;

  while (hasMore) {
    const { data } = await client.query({
      query,
      fetchPolicy: 'no-cache',
      variables: {
        pagination: { pageSize, pageNumber },
        baseCurrencies: vars.baseCurrency,
        effectiveOnStart: vars.effectiveDateFrom,
        effectiveOnEnd: vars.effectiveDateTo,
        keywordSearch: vars.keywordSearch,
        OrderBy: vars.OrderBy
      }
    });

    const items = data?.core?.searchExchangeRates?.items || [];
    all.push(...items);

    hasMore = items.length === pageSize;
    pageNumber += 1;
  }

  return all;
};

export const buildLovs = (lovData: any) => {
  const baseCurrencyList = lovData?.core?.getCurrencies || [];
  const BaseCurrency: Record<string, string> = { [ALL]: 'All' };
  baseCurrencyList.forEach((n: any) => {
    if (n?.code) BaseCurrency[String(n.id)] = String(n.title ?? n.code);
  });

  return { BaseCurrency };
};

const ExportCurrencyRateDrawer: React.FC<IBaseDrawerProps> = ({ onSuccess }) => {
  const client = useApolloClient();

  const { submitButtonState, formDisabled, handleSubmission } =
    useDrawerSubmission();
  const [values, setValues] = useState<IExportCurrencyDrawerFilters>({
    baseCurrency: [ALL],
    effectiveDate: []
  });

  const [lovs, setLovs] = useState<Record<string, Record<string, string>>>({});
  const [sections, setSections] = useState<IDynamicFormWidgetSection[]>([]);

  const { data: lovData } = useGetCurrencies.useQuery();

  useEffect(() => {
    const built = buildLovs(lovData);
    setLovs(built);

    const initialSections = exportCurrencyDrawerSections(values, built);
    setSections(initialSections);
  }, [lovData]);

  const onSubmit = (formValues: any) => {
    const performSubmission = async () => {
      const [data] = normalizeDynamicFormDrawerValues(sections, formValues);

      const selectedBaseCurrencies = normalizeMulti(data.baseCurrency);

      const formattedDateRange = DateService.formatDateTimeRangeBackend(
        data.effectiveDate
      );

      const effectiveDateFrom = formattedDateRange?.[0] || null;
      const effectiveDateTo = formattedDateRange?.[1] || null;

      const typesVar = selectedBaseCurrencies.includes(ALL)
        ? null
        : selectedBaseCurrencies;

      const pickerFrom = data.effectiveDate?.[0];
      const pickerTo = data.effectiveDate?.[1];

      let fileName = 'Redbridge_Currency Exchange Rate';
      if (pickerFrom && pickerTo) {
        fileName += `_${formatForFileName(pickerFrom)}_${formatForFileName(
          pickerTo
        )}`;
      }

      const currencies = await fetchAllCurrencies(client, searchExchangeRates, {
        baseCurrency: typesVar,
        effectiveDateFrom,
        effectiveDateTo,
        keywordSearch: null,
        OrderBy: null
      });

      const filtered = currencies;
      if (!filtered.length) {
        throw new Error(
          'No currency exchange rates found for the selected filters.'
        );
      }

      const excelData = mapItemsToExcelData(filtered);

      downloadExcelFile({
        data: excelData,
        filename: fileName,
        headers: exportCurrencyExcelHeaders,
        sheetName: 'Sheet1',
        groupHeaders: exportCurrencyExcelHeaderGroups,
        styles: exportCurrencyExcelTemplateStyles,
        columnWidths: exportCurrencyExcelColumnWidths,
        freezeHeaderRows: 0
      });

      toast.success(
        <ToastSuccessMessage>
          Currency exchange rate successfully exported
        </ToastSuccessMessage>
      );
    };
    handleSubmission(
      performSubmission,
      () => onSuccess(),
      (error) => {
        console.error('Export error:', error);
        toast.error(
          <ToastErrorMessage>{error.message}</ToastErrorMessage>
        );
      }
    );
  };

  const handleChange = (
    fieldName: keyof IExportCurrencyDrawerFilters,
    value: any,
    sectionIndex: number,
    updatedValues: IExportCurrencyDrawerFilters
  ) => {
    const newValues = { ...updatedValues, [fieldName]: value };
    setValues(newValues);
    setSections(exportCurrencyDrawerSections(newValues, lovs));
  };

  return (
    <DynamicFormWidget
      sections={sections}
      onSubmit={onSubmit}
      disableForm={formDisabled}
      onChange={handleChange}
      submitButtonState={submitButtonState}
    />
  );
};

export { ExportCurrencyRateDrawer };
